/**
 * SettingsProjectsPage — управление проектами.
 *
 * Phase 5 заменит это полноценным UI. Пока — минимум:
 * список, создание, переименование, удаление.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Folder, Plus, Trash2, Edit2, Check, X } from 'lucide-react';

import {
  useProjects,
  useCreateProject,
  useRenameProject,
  useDeleteProject,
} from '@/hooks/useProjects';
import { useProjectStore } from '@/store/projectStore';

export function SettingsProjectsPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useProjects();
  const create = useCreateProject();
  const rename = useRenameProject();
  const del = useDeleteProject();
  const setCurrent = useProjectStore((s) => s.setCurrent);
  const currentId = useProjectStore((s) => s.currentProjectId);

  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    try {
      const created = await create.mutateAsync(name);
      // После создания делаем новый проект текущим — иначе юзер увидит,
      // что проект «создан, но куда-то пропал из Header» (он уже там,
      // просто не выбран). SetCurrent идёт через store + ProjectPicker сам
      // синхронизирует URL.
      setCurrent(created.id);
      setNewName('');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[SettingsProjectsPage] create failed', err);
    }
  };

  const startRename = (project: { id: string; name: string }) => {
    setEditingId(project.id);
    setEditingName(project.name);
  };

  const cancelRename = () => {
    setEditingId(null);
    setEditingName('');
  };

  const submitRename = async (id: string) => {
    if (!editingName.trim()) return;
    await rename.mutateAsync({ id, name: editingName.trim() });
    setEditingId(null);
  };

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold mb-1">{t('settings.projects.title')}</h1>
        <p className="text-sm text-surface-400">{t('settings.projects.subtitle')}</p>
      </div>

      <form onSubmit={handleCreate} className="card flex items-center gap-3">
        <Folder className="w-5 h-5 text-surface-500" />
        <input
          className="input flex-1"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder={t('settings.projects.newPlaceholder')}
        />
        <button
          type="submit"
          disabled={!newName.trim() || create.isPending}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          {t('common.create')}
        </button>
      </form>

      {isLoading && <p className="text-sm text-surface-400">{t('common.loading')}</p>}

      {!isLoading && (data?.items.length ?? 0) === 0 && (
        <div className="card text-sm text-surface-400">
          {t('settings.projects.empty')}
        </div>
      )}

      <div className="space-y-2">
        {data?.items.map((project) => (
          <div
            key={project.id}
            className={`card flex items-center justify-between gap-3 ${
              project.id === currentId
                ? 'border-primary-500/40 bg-primary-500/5'
                : ''
            }`}
          >
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <Folder className="w-5 h-5 text-primary-400 shrink-0" />
              {editingId === project.id ? (
                <input
                  className="input flex-1"
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  autoFocus
                />
              ) : (
                <span className="font-medium truncate">{project.name}</span>
              )}
              {project.id === currentId && (
                <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-primary-500/20 text-primary-300">
                  current
                </span>
              )}
            </div>

            <div className="flex items-center gap-1">
              {editingId === project.id ? (
                <>
                  <button
                    onClick={() => submitRename(project.id)}
                    disabled={rename.isPending}
                    className="p-2 rounded-lg hover:bg-surface-800 text-success"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    onClick={cancelRename}
                    className="p-2 rounded-lg hover:bg-surface-800 text-danger"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setCurrent(project.id)}
                    title={t('settings.projects.activate', { defaultValue: 'Сделать текущим' })}
                    className={`p-2 rounded-lg ${
                      project.id === currentId
                        ? 'text-primary-400'
                        : 'hover:bg-surface-800 text-surface-500'
                    }`}
                  >
                    <Folder className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => startRename(project)}
                    className="p-2 rounded-lg hover:bg-surface-800 text-surface-400"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => del.mutate(project.id)}
                    disabled={del.isPending}
                    className="p-2 rounded-lg hover:bg-surface-800 text-danger"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
